import dayjs from 'dayjs';
import React from 'react';
import { ChevronLeft, ChevronRight } from '@carbon/react/icons';
import MonthGrid, { CalendarDay } from 'src/components/Calendar/MonthGrid';

export type { CalendarDay } from 'src/components/Calendar/MonthGrid';

interface RangeCalendarProps {
  /** First of the two visible months, `YYYY-MM`. */
  month: string;
  onMonthChange: (month: string) => void;
  start: CalendarDay | null;
  end: CalendarDay | null;
  /** Called with the new range; `end` is null while only the first day of a range is picked. */
  onChange: (start: CalendarDay, end: CalendarDay | null) => void;
  /** Today in the restaurant's timezone, so the marker is the restaurant's today. */
  today: CalendarDay;
  locale: string;
  labels: { previousMonth: string; nextMonth: string };
}

/**
 * Two month grids, always visible, for picking a range by clicking its two ends.
 *
 * Its own component rather than Carbon's DatePicker: that wraps flatpickr, passes `inline`
 * through but not `showMonths`, and styles its calendar as a single-month dropdown — an
 * always-open two-month range picker meant fighting both. This is plain dayjs and Carbon
 * tokens, so it themes with the portal and can be driven from a jsdom test.
 *
 * Days are `YYYY-MM-DD` strings throughout. The caller converts to and from instants in the
 * restaurant's timezone at the edges (see DateRangeMenu), so nothing in here can shift a day.
 * Weeks start on Monday, as the dashboard's weekly preset does.
 */
const RangeCalendar = ({ month, onMonthChange, start, end, onChange, today, locale, labels }: RangeCalendarProps) => {
  const first = dayjs(`${month}-01`);
  const months = [first, first.add(1, 'month')];

  const handleDay = (day: CalendarDay) => {
    // A complete range (or none) starts over; a half-picked one is completed, in either order.
    if (!start || end) {
      onChange(day, null);
    } else if (day < start) {
      onChange(day, start);
    } else {
      onChange(start, day);
    }
  };

  const monthTitle = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });

  return (
    <div className="range-calendar">
      <div className="range-calendar__nav">
        <button
          type="button"
          className="range-calendar__nav-button"
          aria-label={labels.previousMonth}
          onClick={() => onMonthChange(first.subtract(1, 'month').format('YYYY-MM'))}
        >
          <ChevronLeft size={16} aria-hidden />
        </button>
        <button
          type="button"
          className="range-calendar__nav-button"
          aria-label={labels.nextMonth}
          onClick={() => onMonthChange(first.add(1, 'month').format('YYYY-MM'))}
        >
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>
      <div className="range-calendar__months">
        {months.map((current) => (
          <div className="range-calendar__month" key={current.format('YYYY-MM')}>
            <div className="range-calendar__title">{monthTitle.format(current.toDate())}</div>
            <MonthGrid
              block="range-calendar"
              month={current.format('YYYY-MM')}
              today={today}
              locale={locale}
              dayModifiers={(day) => {
                const isEdge = day === start || day === end;
                const inRange = Boolean(start && end && day > start && day < end);
                return [isEdge ? 'selected' : '', inRange ? 'in-range' : ''].filter(Boolean);
              }}
              dayPressed={(day) => day === start || day === end || Boolean(start && end && day > start && day < end)}
              onDayClick={handleDay}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default RangeCalendar;
